import { isDate, isNullish, isObject } from "radashi";

type DeepNonNullable<T> = T extends null | undefined
  ? never
  : T extends Array<infer U>
    ? Array<DeepNonNullable<U>>
    : T extends object
      ? {
          [
            K in keyof T as T[K] extends null | undefined ? never : K
          ]: DeepNonNullable<T[K]>;
        }
      : T;

export function entries<T extends object>(obj: T): [keyof T, T[keyof T]][] {
  return Object.entries(obj) as [keyof T, T[keyof T]][];
}

export function removeNullish<T>(obj: T): DeepNonNullable<T> {
  if (isNullish(obj)) {
    throw new Error("Cannot remove nullish properties from nullish object.");
  }

  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== null && item !== undefined)
      .map((item) => removeNullish(item)) as DeepNonNullable<T>;
  }

  if (isDate(obj)) {
    return obj as DeepNonNullable<T>;
  }

  if (isObject(obj)) {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (isNullish(value)) {
        continue;
      }

      result[key] = removeNullish(value);
    }

    return result as DeepNonNullable<T>;
  }

  return obj as DeepNonNullable<T>;
}

export function safeParseJSON<T extends object>(
  value: string,
  defaultValue?: T,
): T | null {
  try {
    return JSON.parse(value);
  } catch {
    return defaultValue ?? null;
  }
}
